/**
 * Runtime switches for the public demo.
 *
 * Only one so far, and it exists because a single org-level boolean controls a
 * bundle of things that do not obviously belong together:
 *
 *   ON  - Developer League and Test Champions are explorable, Developer
 *         Profiles shows its ranked table, Awards shows full rankings, and an
 *         individual profile shows Contributor Reliability, Work Breakdown,
 *         Language Distribution, Active branches AND Lines-of-Code cards.
 *   OFF - none of that, and /developer-league renders the "Individual ranking
 *         is off" explainer instead.
 *
 * It ships ON. The deciding factor is that four of those profile sections -
 * Contributor Reliability, Work Breakdown, Language Distribution and Active
 * branches - rank nobody, and they are most of what makes an individual
 * profile worth showing at all. Turning the flag off to avoid the
 * Lines-of-Code cards takes the profile from six sections to three, which is
 * too much of the product to give up for one card.
 *
 * What that card costs us is real and worth naming: brand guidelines reject
 * LoC as a vanity metric, and it cannot be suppressed from fixtures because
 * `loc_per_day` is in `ZERO_WHEN_IDLE_METRIC_KEYS` - omitting the value renders
 * `0`, not nothing. It sits behind an explicit org opt-in, beside review load
 * and cycle time, and carries no rank, which is a materially different
 * statement from ranking people by it.
 *
 * Ranking never appears either way. The demo omits the optional
 * `TeamComparison` and `team_percentile_*` fields, so no rank, percentile or
 * vs-team line renders regardless of this switch - see
 * `generators/developerDetail.ts`.
 *
 * The escape hatch turns it OFF, for showing the team-first framing to someone
 * who asks about surveillance, or for checking the gated surfaces degrade
 * properly:
 *
 *   localStorage.setItem('codepulse:demo:individual-metrics', 'off')
 *
 * Deliberately localStorage and not a query param - a URL gets shared, indexed
 * or pasted into a deck, and the default is a positioning decision rather than
 * a preference.
 */

/** Set to `'off'` in localStorage to disable individual metrics in the demo. */
export const DEMO_INDIVIDUAL_METRICS_KEY = 'codepulse:demo:individual-metrics';

/**
 * Whether the demo org opts in to individual metrics. On unless explicitly
 * turned off.
 *
 * Matches `'off'` exactly rather than treating any value as a disable: a stray
 * string left in storage must not quietly change what the demo says about us.
 *
 * Wrapped in try/catch because `localStorage` throws outright in some privacy
 * modes, and the demo has to render for a visitor who blocks site data - that
 * is a large share of exactly the anonymous traffic this route exists for.
 */
export function demoIndividualMetricsEnabled(): boolean {
  try {
    return localStorage.getItem(DEMO_INDIVIDUAL_METRICS_KEY) !== 'off';
  } catch {
    return true;
  }
}
